import React from 'react';
import {useTranslation} from 'react-i18next';
import api from '../api';
import {
  ErrorModalProps,
  Statuses,
  useAbortController,
  useErrorModal,
  useIsMounted,
  useStatus,
} from '../utils/hooks';
import {ErrorType} from '../utils/common';
import scanningErrorIcon from '../assets/scanning_doc_error.svg';

const MaxScanFailureAttemptsBeforeFormProceed = 3;
const DetectingResponsesGapS = 1;
const ResetStatusTimeoutMs = 2000;
const CheckTimeoutMs = 2000;

enum DetectingStatuses {
  completed = 'COM',
  error = 'ERR',
  processing = 'PRO',
  new = 'NEW',
}

// const ExampleMrzImage = styled.img`
//   display: block;
//   margin: 15px auto 0;
//
//   @media (max-width: ${device.mobileL}) {
//     max-width: 240px;
//     max-height: 160px;
//   }
// `;

type MrzErrorModalProps = {
  docType?: string;
  nationality?: string;
};

type HandleMRZDetection = {
  reservationId: string;
  onSuccess: (image: string, data: any) => void;
  image?: string;
  onError?: (error: ErrorType) => void;
};

type HandleStatusDetection = {
  data: any;
  image: string;
  error: ErrorType;
  aborted?: boolean;
};

function useMRZDetection() {
  const isMounted = useIsMounted();
  const {status, setStatus} = useStatus();
  const {ErrorModal, displayError} = useErrorModal();
  const timeoutRef = React.useRef<any>();
  const [failedAttempts, setFailedAttempts] = React.useState(0);
  const onSuccessRef = React.useRef<((image: string, data: any) => void) | null>(null);
  const onErrorRef = React.useRef<((error: ErrorType) => void) | undefined>();
  const statusTimeoutRef = React.useRef<any>();
  const isMaxFailedAttemptsReached =
    failedAttempts >= MaxScanFailureAttemptsBeforeFormProceed;
  const {abortController, setupAbortController} = useAbortController();
  const {t} = useTranslation();

  React.useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(statusTimeoutRef.current);
      abortController.abort();
    };
  }, [abortController]);

  const handleDetectionError = React.useCallback(
    (error: any = null, aborted = false) => {
      if (!aborted) {
        setFailedAttempts(prevState => prevState + 1);
        onErrorRef.current?.(error);
        statusTimeoutRef.current = setTimeout(() => {
          setStatus(Statuses.idle);
        }, ResetStatusTimeoutMs);
      }
    },
    [setStatus],
  );

  const handleStatusDetection = React.useCallback(
    ({data, image, error, aborted}: HandleStatusDetection) => {
      if (!isMounted.current) {
        return null;
      }

      if (error) {
        handleDetectionError(error, aborted);
        setStatus('error');
        return null;
      }

      if (data?.status === DetectingStatuses.completed) {
        if (data?.mrz_detected && data?.valid_composite) {
          onSuccessRef.current?.(image, data);
          setStatus('success');
        } else {
          handleDetectionError({
            message: t('could_not_detect'),
          });
          setStatus('error');
        }

        return null;
      }

      if (data?.status === DetectingStatuses.error) {
        setStatus('error');
        handleDetectionError({
          message: t('could_not_detect'),
        });
        return null;
      }
      return true;
    },
    [handleDetectionError, isMounted, setStatus, t],
  );

  const checkMRZDetection = React.useCallback(
    async (id: string, image: string) => {
      if (!id) {
        handleDetectionError({
          message: 'MRZ Check Id is missing.',
        });
        return;
      }

      const {data, error, aborted} = await api.ocr.checkImageMRZDetection(id, {
        signal: setupAbortController(),
      });

      const resultStatus = handleStatusDetection({
        data,
        image,
        error,
        aborted,
      });
      if (!resultStatus) return;

      return new Promise(resolve => {
        timeoutRef.current = setTimeout(() => {
          resolve(checkMRZDetection(id, image));
        }, CheckTimeoutMs * DetectingResponsesGapS);
      });
    },
    [handleDetectionError, handleStatusDetection, setupAbortController],
  );

  const startMRZDetection = React.useCallback(
    async ({image = '', reservationId = '', onSuccess, onError}: HandleMRZDetection) => {
      setStatus('loading');
      onSuccessRef.current = onSuccess;
      onErrorRef.current = onError;
      const {data, error, aborted} = await api.ocr.sendImageForMRZDetection(
        image,
        reservationId,
        {
          signal: setupAbortController(),
        },
      );

      const resultStatus = handleStatusDetection({
        data,
        image,
        error,
        aborted,
      });
      if (!resultStatus) return;

      await checkMRZDetection(data?.id, image);
    },
    [checkMRZDetection, handleStatusDetection, setStatus, setupAbortController],
  );

  const MrzErrorModal = React.useCallback(
    ({docType, nationality, ...props}: MrzErrorModalProps & ErrorModalProps) => {
      // const exampleMrzImage = getExapmleDocumentImageMrz(docType, nationality);
      return (
        <ErrorModal
          iconSrc={scanningErrorIcon}
          title={<>{t('error')} &#128542;</>}
          message={t('could_not_detect_mrz')}
          description={
            <>
              <span>{t('try_again_mrz_or_contact')}</span>
              {/*<ExampleMrzImage*/}
              {/*  width={320}*/}
              {/*  height={200}*/}
              {/*  src={exampleMrzImage}*/}
              {/*  alt="exampleMrzImage"*/}
              {/*/>*/}
            </>
          }
          {...props}
        />
      );
    },
    [ErrorModal, t],
  );

  return {
    startMRZDetection,
    isMaxFailedAttemptsReached,
    abortController,
    MrzErrorModal,
    displayMrzError: displayError,
    status,
    // error,
  };
}

export {useMRZDetection};
